import { ChainIdEnum } from "../../enums/chain";
import { WalletProviderKind } from "../../enums/walletProvider";
import { CHAIN_CONFIGS } from "./configs";
import { getChainIdFromEnv } from "./getters";
import { NativeCurrency } from "./types";

// =============================================================================
// Multichain helpers (for Socket deposit chains)
// =============================================================================

/**
 * Chains that support Socket deposits
 */
export const SOCKET_DEPOSIT_CHAIN_IDS: ChainIdEnum[] = [
  ChainIdEnum.OPTIMISM,
  ChainIdEnum.OPTIMISM_TESTNET,
  ChainIdEnum.ARBITRUM,
  ChainIdEnum.ARBITRUM_TESTNET,
];

/**
 * Check if a chain supports Socket deposits
 */
export const isSocketDepositChain = (chainId: ChainIdEnum): boolean => {
  return SOCKET_DEPOSIT_CHAIN_IDS.includes(chainId);
};

/**
 * Get all chains that have multichain (Socket) configuration
 */
export const getMultichainChainIds = (): ChainIdEnum[] => {
  return Array.from(CHAIN_CONFIGS.entries())
    .filter(([_, config]) => config.contracts.multichain !== undefined)
    .map(([chainId]) => chainId);
};

/**
 * Get all chains that have perpetual DEX configuration
 */
export const getPerpetualDexChainIds = (): ChainIdEnum[] => {
  return Array.from(CHAIN_CONFIGS.entries())
    .filter(([_, config]) => config.contracts.perpetualDex !== undefined)
    .map(([chainId]) => chainId);
};

// =============================================================================
// Wallet/Connector helpers
// =============================================================================

/**
 * Get supported chain for the current environment
 * Based on REACT_APP_CHAIN_ID
 */
export const getSupportedChain = (): ChainIdEnum => {
  return getChainIdFromEnv();
};

/**
 * Resolve the wallet backend for a chain (defaults to the active env chain).
 * Chains that don't declare `walletProvider` fall back to WEB3REACT, preserving
 * the historical injected / WalletConnect / Coinbase / EIP-6963 behavior.
 */
export const getWalletProviderKind = (
  chainId?: ChainIdEnum
): WalletProviderKind => {
  const id = chainId ?? getChainIdFromEnv();
  const configured =
    CHAIN_CONFIGS.get(id)?.walletProvider ?? WalletProviderKind.WEB3REACT;
  // Keep the existing DogeOS wallet flow available until DogeOS issues this
  // deployment a partner client ID. Supplying the ID enables the SDK normally.
  if (
    configured === WalletProviderKind.DOGEOS &&
    !process.env.REACT_APP_DOGEOS_CLIENT_ID?.trim()
  ) {
    return WalletProviderKind.WEB3REACT;
  }
  return configured;
};

/**
 * Get supported deposit chains for the current environment
 */
export const getSupportedDepositChains = (): ChainIdEnum[] => {
  const currentChain = getChainIdFromEnv();
  const currentConfig = CHAIN_CONFIGS.get(currentChain);

  // Start with current chain
  const chains: ChainIdEnum[] = [currentChain];

  // No cross-chain deposit sources: the OP/Arbitrum options were Aevo's Socket
  // bridge, which MarginX never deployed (see SOCKET_DEPOSIT_CHAINS — empty for
  // the same reason). Every deployment deposits on its native chain only. The
  // old exclusion list missed BNB_TESTNET, which is how dead bridge tiles kept
  // rendering there. currentConfig retained for future per-chain gating.
  void currentConfig;

  return chains;
};

/**
 * Get AddEthereumChainParameter for wallet
 */
export const getAddChainParameters = (
  chainId: ChainIdEnum
):
  | {
      chainId: number;
      chainName: string;
      nativeCurrency: NativeCurrency;
      rpcUrls: string[];
      blockExplorerUrls: string[];
    }
  | number => {
  const config = CHAIN_CONFIGS.get(chainId);

  if (!config || !config.nativeCurrency) {
    return chainId;
  }

  return {
    chainId: config.chainId,
    chainName: config.name,
    nativeCurrency: config.nativeCurrency,
    rpcUrls: config.rpcUrls,
    blockExplorerUrls: [config.explorerUrl],
  };
};
